import React, { useEffect, useState } from 'react';
import { Container, Row, Col, Image, Button } from 'react-bootstrap';
import CourseItem from '../../common/courseItem/CourseItem';
import heroImage from '../../../assets/images/rimettiti-al-centro/rimettiti-al-centro-profile.jpg';
import './RimettitiAlCentro.css';
import { courseSections } from '../../data/courseData';
import thinkNowImage from '../../../assets/images/rimettiti-al-centro/finale.jpg';
import { useCart } from '../../../contexts/CartContext';
import { courseService } from '../../../services';
import type { PublicCourse } from '../../../types/cart';
import WelfareBanner from '../../common/welfareBanner/WelfareBanner';

const RimettitiAlCentro: React.FC = () => {
  const [showModal, setShowModal] = useState(false);
  const [expandedCourseId, setExpandedCourseId] = useState<string>('be-smart');
  const { addItem, isInCart, openCart } = useCart();

  // Prezzo reale dal database (mai statico): senza questo, il sito mostrava
  // sempre il prezzo hardcoded finché non si arrivava al checkout Stripe.
  const [catalog, setCatalog] = useState<PublicCourse[]>([]);

  useEffect(() => {
    courseService
      .getCatalog()
      .then(setCatalog)
      .catch(() => {
        // Se il catalogo non carica, il pulsante resta disabilitato
        // (vedi catalogLoaded più sotto) invece di aggiungere un prezzo errato.
      });
  }, []);

  const handleDownloadRequest = () => {
    setShowModal(true);
  };

  const handleCourseToggle = (courseId: string) => {
    setExpandedCourseId(expandedCourseId === courseId ? '' : courseId);
  };

  // Aggiunge un corso al carrello per id, con il prezzo reale appena caricato.
  // Se già presente, apre semplicemente il drawer.
  const handleAddToCart = (courseId: string) => {
    const course = catalog.find((c) => c.id === courseId);
    if (!course || course.price === null) return;
    if (isInCart(courseId)) {
      openCart();
    } else {
      addItem({
        id: course.id,
        title: course.title,
        subtitle: course.subtitle ?? undefined,
        price: course.price,
        imageUrl: course.imageUrl ?? undefined,
      }); // addItem apre il drawer automaticamente
    }
  };

  // Sovrascrive le action dei ctaButtons con la logica carrello.
  // courseData.ts rimane pulito — le action arrivano dall'esterno.
  const catalogLoaded = catalog.some((c) => c.id === 'be-smart');
  const sectionsWithCartActions = courseSections.map((section) => {
    if (section.id === 'be-smart') {
      return {
        ...section,
        ctaButtons: {
          primary: {
            text: isInCart('be-smart')
              ? '✓ Nel carrello'
              : catalogLoaded
                ? 'AGGIUNGI AL CARRELLO'
                : 'Caricamento…',
            action: () => handleAddToCart('be-smart'),
          },
          secondary: {
            text: 'CONTATTAMI SUBITO',
            // Rimanda alla sezione contatti in home o apre mailto
            action: () => window.open('https://wa.me/3757481380', '_blank'),
          },
        },
      };
    }
    return section;
  });

  return (
    <div className="academy-container">
      {/* Hero Section */}
      <section className="rimetti-al-centro-hero py-5">
        <Container>
          <Row className="align-items-center min-vh-60 py-5">
            <Col lg={6}>
              <div className="hero-content">
                <h1 className="hero-title display-3 fw-bold mb-4">
                  <span className="text-gradient-primary">
                    Rimettiti al centro
                  </span>
                </h1>
                <p className="hero-description lead text-muted mb-4">
                  Inizia subito a vivere meglio!
                  <br />
                  Ti presento i SERVIZI DIGITALI di BENESSERE POSTURALE per
                  riportarti subito AL CENTRO:
                </p>
              </div>
            </Col>

            <Col lg={6}>
              <div className="hero-image-container">
                <div className="image-wrapper position-relative">
                  <div className="main-image-container text-center">
                    <Image
                      src={heroImage}
                      alt="Rimettiti al centro - Tutti i servizi di benessere posturale"
                      className="rimetti-hero-image"
                      fluid
                    />
                  </div>
                </div>
              </div>
            </Col>
          </Row>
        </Container>
      </section>

      {/* Welfare Banner */}
      <section className="banner-section">
        <div className="container">
          <WelfareBanner />
        </div>
      </section>

      {/* Lista dei corsi */}
      {sectionsWithCartActions.map((course, index) => (
        <CourseItem
          key={course.id}
          course={course}
          index={index}
          isExpanded={expandedCourseId === course.id}
          onToggle={() => handleCourseToggle(course.id)}
          handleDownloadRequest={handleDownloadRequest}
        />
      ))}

      {/* Sezione finale */}
      <section className="final-section py-5 bg-dark text-white">
        <Container>
          <Row className="mb-5">
            <Col className="text-center">
              <h2 className="fw-bold mb-3" style={{ fontSize: '2.5rem' }}>
                Se vuoi scegliere di sentirti BENE in futuro,
              </h2>
              <div className="think-section">
                <h1 className="display-4 fw-bold text-warning mb-2">
                  PENSACI PRIMA!
                </h1>
                <h1 className="display-4 fw-bold text-primary">
                  PENSACI ADESSO!
                </h1>
              </div>
            </Col>
          </Row>

          <Row className="mb-5">
            <Col className="text-center">
              <div className="think-image-container d-inline-block">
                <Image
                  src={thinkNowImage}
                  alt="Pensaci ora - Il momento di agire è adesso"
                  className="think-now-image"
                  roundedCircle
                  style={{
                    width: '300px',
                    height: '300px',
                    objectFit: 'cover',
                    border: '8px solid white',
                    boxShadow: '0 15px 40px rgba(0,0,0,0.3)',
                  }}
                />
                <div className="image-glow"></div>
              </div>
            </Col>
          </Row>

          <Row>
            <Col className="text-center">
              <h2 className="fw-bold mb-3" style={{ fontSize: '2.5rem' }}>
                Inizia SUBITO a VIVERE MEGLIO:
              </h2>
              <div className="think-section">
                <h1 className="display-4 fw-bold text-warning mb-2">
                  VIVI A TESTA ALTA!
                </h1>
              </div>
            </Col>
          </Row>

          <Row className="mt-5">
            <Col className="text-center">
              <p className="lead mb-4">
                Non aspettare che i dolori arrivino. Inizia oggi il tuo percorso
                verso un benessere duraturo e consapevole.
              </p>
              <div className="d-flex flex-wrap justify-content-center gap-3">
                <Button
                  variant="warning"
                  size="lg"
                  onClick={() => handleAddToCart('be-smart')}
                  className="px-5 py-3"
                >
                  {isInCart('be-smart')
                    ? '✓ BE SMART NEL CARRELLO'
                    : '🛒 ACQUISTA BE SMART ORA'}
                </Button>
                <Button
                  variant="outline-light"
                  size="lg"
                  as="a"
                  href="https://wa.me/3757481380"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="px-5 py-3"
                >
                  📞 CONTATTAMI PER UNA CONSULENZA
                </Button>
              </div>
            </Col>
          </Row>
        </Container>
      </section>
    </div>
  );
};

export default RimettitiAlCentro;
